import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { ConfirmCheckout } from '@/modules/auth/use-cases'
import type { CreateOrgType, SubscriptionTier } from '@/shared/domain'
import { reportError } from '@/shared/infra/observability'

export const CREATE_ORG_MOBILE_DATA_STORAGE_KEY = 'createOrgMobileData'

const CONFIRM_CHECKOUT_FAILED_MESSAGE =
  'We could not finish setting up your organization. Please contact support.'

export type RegisterCompleteStatus = 'loading' | 'success' | 'error'
export type RegisterCompleteSource = 'mobile' | 'web'

type OrgSessionData = {
  orgType: CreateOrgType
  orgName: string
  orgState: string
  orgCountry: string
  billingCycle: SubscriptionTier
  promoCode: string
  accessToken?: string
  source?: RegisterCompleteSource
}

export type UseRegisterCompletePageProps = {
  onConfirmCheckout: ConfirmCheckout
}

export type UseRegisterCompletePageResult = {
  status: RegisterCompleteStatus
  source: RegisterCompleteSource
  errorMessage: string
  handleContinue: () => void
}

function readOrgData(): OrgSessionData | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(CREATE_ORG_MOBILE_DATA_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as OrgSessionData
  } catch {
    return null
  }
}

export function useRegisterCompletePage({
  onConfirmCheckout,
}: UseRegisterCompletePageProps): UseRegisterCompletePageResult {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const processedRef = useRef(false)

  const [status, setStatus] = useState<RegisterCompleteStatus>('loading')
  const [source, setSource] = useState<RegisterCompleteSource>('web')
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (processedRef.current) return

    const sessionId = searchParams.get('session_id')
    if (!sessionId) {
      navigate('/register', { replace: true })
      return
    }

    processedRef.current = true

    const orgData = readOrgData()
    if (!orgData) {
      setStatus('error')
      setErrorMessage('Organization details not found. Please start over.')
      return
    }

    const resolvedSource = orgData.source ?? 'web'
    setSource(resolvedSource)

    const token = orgData.accessToken
    if (!token) {
      setStatus('error')
      setErrorMessage('Session expired. Please start over.')
      return
    }

    void (async () => {
      try {
        const confirmation = await onConfirmCheckout({
          sessionId,
          orgType: orgData.orgType,
          accessToken: token,
        })
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(CREATE_ORG_MOBILE_DATA_STORAGE_KEY)
        }
        setStatus('success')
        if (resolvedSource === 'mobile') {
          const createdOrg = (confirmation as unknown as { org?: { id?: string; type?: string } } | undefined)
            ?.org
          window.flutter_inappwebview?.callHandler?.('orgCreated', {
            orgId: createdOrg?.id,
            orgType: createdOrg?.type ?? orgData.orgType,
          })
        }
      } catch (err) {
        const message = reportError(err, {
          fallbackMessage: CONFIRM_CHECKOUT_FAILED_MESSAGE,
        })
        setStatus('error')
        setErrorMessage(message)
        if (resolvedSource === 'mobile') {
          window.flutter_inappwebview?.callHandler?.('orgError', { message })
        }
      }
    })()
  }, [searchParams, navigate, onConfirmCheckout])

  const handleContinue = () => {
    navigate('/dashboard')
  }

  return {
    status,
    source,
    errorMessage,
    handleContinue,
  }
}
